import {
  Button,
  Container,
  Heading,
  Icon,
  Span,
  Table,
  Text,
  useModal,
} from "@fluidattacks/design";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "styled-components";

import type { IVulnerabilityPolicies } from "./types";
import { getColumns } from "./utils";
import { AddVulnerabilityTypeModal } from "./vulnerability-type-modal";

import type { IVulnerabilityPoliciesData } from "../types";
import { useTable } from "hooks/use-table";

const prefix = "organization.tabs.policies.findings.";

const VulnerabilitiesPolicies = ({
  data,
  organizationId,
  userRole,
  vulnerabilityTreatment,
}: IVulnerabilityPolicies): JSX.Element => {
  const { t } = useTranslation();
  const theme = useTheme();
  const modalRef = useModal("add-vulnerability-type");
  const treatment =
    vulnerabilityTreatment === "ACCEPTED" ? "temporary" : "permanent";
  const [search, setSearch] = useState("");
  const policiesData = useMemo((): IVulnerabilityPoliciesData[] => {
    return data.filter((policy): boolean =>
      policy.name.toLowerCase().includes(search.toLowerCase()),
    );
  }, [data, search]);

  const handleSearch = useCallback((value: string): void => {
    setSearch(value);
  }, []);

  const { table } = useTable({
    columns: getColumns(prefix, organizationId, userRole),
    data: policiesData,
    id: "vulns-policies",
    options: {
      actionsOptions: { onSearch: handleSearch },
      columnOptions: { sorting: [{ desc: true, id: "status" }] },
      filtersMode: "client-side",
      paginationMode: "client-side",
    },
    total: data.length,
  });

  const handleClick = useCallback((): void => {
    modalRef.toggle();
  }, [modalRef]);

  return (
    <Container
      display={"flex"}
      flexDirection={"column"}
      gap={1}
      px={1.25}
      py={1}
    >
      <Container display={"flex"} flexDirection={"column"} gap={0.5}>
        <Heading lineSpacing={1.75} size={"sm"}>
          {t(`${prefix}title.${treatment}`)}
        </Heading>
        <Container alignItems={"center"} display={"flex"}>
          <Icon
            icon={"circle-info"}
            iconColor={theme.palette.gray[600]}
            iconSize={"sm"}
            iconType={"fa-light"}
            mr={0.25}
          />
          <Span size={"md"}>
            <Text display={"inline"} size={"md"}>
              {t(`${prefix}subtitle.${treatment}.text`)}
            </Text>
            <Text display={"inline"} fontWeight={"bold"} size={"md"}>
              {t(`${prefix}subtitle.${treatment}.highlight`)}
            </Text>
          </Span>
        </Container>
      </Container>
      <Table table={table}>
        <Table.Options>
          <Table.Controls>
            <Container id={"actions-container"}>
              <Button
                icon={"plus"}
                id={`add-${treatment}-policies`}
                onClick={handleClick}
              >
                {t(`${prefix}button`)}
              </Button>
            </Container>
          </Table.Controls>
        </Table.Options>
        <Table.Content />
        <Table.Footer />
      </Table>
      {modalRef.isOpen && (
        <AddVulnerabilityTypeModal
          data={data}
          modalRef={modalRef}
          organizationId={organizationId}
          vulnerabilityTreatment={vulnerabilityTreatment}
        />
      )}
    </Container>
  );
};

export { VulnerabilitiesPolicies };
